/**
 * Who can own a row, and which of them are people who log in.
 *
 * The Recruiter filter has three consumers — the Daily Calls Report, the
 * Interested list and the Joining Tracker — and they had drifted: two asked
 * for inactive accounts and one did not, so the same filter offered a
 * different set of names depending on which page you were reading. This is the
 * one list, so that cannot happen again.
 *
 * **Historical accounts are included, and grouped rather than removed.** The
 * importer makes the sheet's Recruiter column authoritative: a name matching
 * no user here gets a deactivated account and owns its rows, so
 * `recruiter_user_id` on thousands of imported calls, submissions and
 * placements points at one of them. Dropping them from the filter would make
 * those rows reachable only by clearing the filter entirely — the work would
 * still be there, and there would be no way to ask for one person's share of
 * it.
 *
 * They are told apart by their address. `create_historical_user` mints one
 * under a domain RFC 2606 reserves and guarantees will never resolve, which is
 * a convention in one module rather than a column on the table — see the
 * backlog. It is enough to group a dropdown by and not enough to hang a
 * permission on, which is all that is done with it here.
 *
 * The distinction is worth drawing because "(former)" alone conflated two
 * different things: a colleague who left, whose account was real and whose
 * rows are their own work, and a name that only ever existed because it was a
 * column in a spreadsheet.
 */
import type { Role, User } from '../types';

/** Reserved by RFC 2606. Mirrors HISTORICAL_EMAIL_DOMAIN on the server. */
export const HISTORICAL_EMAIL_DOMAIN = '@historical.invalid';

/**
 * Everyone except employees.
 *
 * A manager or an admin can log a call too, and a filter that offered
 * recruiters alone would hide rows it should show. Employees are the ones who
 * never own recruitment work.
 */
const OWNS_WORK: Role[] = ['super_admin', 'company_admin', 'manager', 'recruiter'];

export interface GroupedRecruiters {
  /** People with a login here, past or present. */
  current: User[];
  /** Names that exist because a spreadsheet carried them. */
  historical: User[];
}

export function isHistorical(user: User): boolean {
  return (user.email ?? '').toLowerCase().endsWith(HISTORICAL_EMAIL_DOMAIN);
}

/**
 * Split the staff list into the two groups the dropdown shows.
 *
 * Sorted by name within each group rather than left in id order, because a
 * list of seventy-odd names is scanned rather than read.
 */
export function groupRecruiters(users: User[]): GroupedRecruiters {
  const owners = users.filter((u) => OWNS_WORK.includes(u.role));
  const byName = (a: User, b: User) =>
    (a.fullName ?? '').localeCompare(b.fullName ?? '', 'en-IN');

  return {
    current: owners.filter((u) => !isHistorical(u)).sort(byName),
    historical: owners.filter(isHistorical).sort(byName),
  };
}

/**
 * What to append to a name in the list.
 *
 * Only for real accounts. Every historical one is inactive by construction, so
 * "(former)" on all of them would be noise inside a group whose label already
 * says what they are.
 */
export function recruiterSuffix(user: User): string {
  if (isHistorical(user)) return '';
  return user.isActive ? '' : ' (former)';
}
